import { Box, Unstable_TrapFocus as FocusTrap, Popper as MuiPopper } from '@mui/material';
import { Theme } from '@radix-ui/themes';
import React, { useEffect } from 'react';
import useIsMobile from '../../hooks/useIsMobile.js';
import Icon from '../Icon/index.js';
import Typography from '../Typography/index.js';

export interface PopperProps {
  onClickClose: () => void;
  isOpen: boolean;
  title?: string;
  children: React.ReactNode;
  anchorEl?: HTMLButtonElement;
}

export default function Popper({
  onClickClose,
  isOpen,
  title,
  children,
  anchorEl,
}: PopperProps): JSX.Element {
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onClickClose();
      }
    };

    addEventListener('keydown', handleEscapeKey);

    return () => removeEventListener('keydown', handleEscapeKey);
  }, [onClickClose]);

  return (
    <MuiPopper
      open={isOpen}
      placement="right"
      anchorEl={!isMobile && anchorEl}
      className="z-20 w-full"
      translate={undefined}
      nonce={undefined}
    >
      <Theme accentColor="indigo">
        <FocusTrap open={isOpen}>
          <Box
            className="h-screen bg-white shadow overflow-auto lg:max-w-max lg:min-w-[18rem] lg:rounded-lg lg:h-auto lg:ml-4"
            tabIndex={-1}
          >
            <div
              className={'p-5 lg:py-3 leading-6 lg:text-center flex items-start justify-between'}
            >
              <div className="relative flex flex-col px-1 pt-20 lg:pt-0 text-left">
                {title ? (
                  <Typography variant={isMobile ? 'h1' : 'h3'} tag="h1">
                    {title}
                  </Typography>
                ) : null}
                {children}
              </div>
              <button
                type="button"
                className="cursor-pointer"
                onClick={onClickClose}
                aria-label="close"
              >
                <Icon type="close" size="LG" responsiveSize="ResponsiveMD" />
              </button>
            </div>
          </Box>
        </FocusTrap>
      </Theme>
    </MuiPopper>
  );
}
